import { DriverScoreRequester, type FleetScore, GeocodeRequester, RiskEventsRequester, type RiskEventWithAddress } from '@cobliteam/api-lib';
import type { TimestampInMillis, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues

const FLEET_SCORE_KEY = 'FLEET-SCORE';
const RISK_EVENT_KEY = 'RISK-EVENT-WITH-ADDRESS';

const useDriverBehavior = () => {
  const useFleetScore = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const getDriverScoreRequester = () => new DriverScoreRequester({ host: env.herbieApiHost });
    return useQuery<FleetScore>({
      queryKey: [FLEET_SCORE_KEY],
      queryFn: () => getDriverScoreRequester().getFleetScore(),
      meta: {
        errorMessage: 'Houve um erro ao carregar a pontuação da frota, tente novamente mais tarde.',
      },
    });
  };

  const useRiskEvents = (selectedDate: TimestampInMillis, driverId: UUID) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const getGeocodeRequester = () => new GeocodeRequester({ host: env.herbieApiHost });
    const getRiskEventRequester = () => new RiskEventsRequester({ host: env.herbieApiHost });

    return useQuery<RiskEventWithAddress[]>({
      queryKey: [RISK_EVENT_KEY],
      queryFn: async () => {
        const period = {
          start: moment(selectedDate).startOf('day').valueOf(),
          end: moment(selectedDate).endOf('day').valueOf(),
        };

        const riskEvents = await getRiskEventRequester().getRiskEvents(period, driverId);

        const latLongs = riskEvents.map((riskEvent: any) => ({
          lat: riskEvent.latitude,
          lng: riskEvent.longitude,
        }));

        const addresses = await getGeocodeRequester().getAddresses(latLongs);

        return riskEvents.map((riskEvent: any, index: number) => ({
          address: addresses[index],
          eventTimestamp: riskEvent.eventTimestamp,
          eventType: riskEvent.eventType,
        }));
      },
      meta: {
        errorMessage: 'Houve um erro ao carregar os eventos de risco, tente novamente mais tarde.',
      },
    });
  };

  return {
    useFleetScore,
    useRiskEvents,
  };
};

export default useDriverBehavior;
